import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import { ORG_GROUP_TYPE } from "@forgepoint/shared";

export const identify = ({
  userId,
  email,
  name,
}: {
  userId: string;
  email: string;
  name: string;
}) => {
  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    posthog.identify(userId, { email: email, name: name });
  }
  Sentry.setUser({ id: userId, email: email, name: name });
};

/**
 * Associates the current user with an organization for PostHog group
 * analytics. Mirrors the backend `company` group type used for
 * organization-scoped feature flag evaluation.
 * @param orgId - The organization ID.
 * @param properties - Optional organization properties (name, etc.).
 * @returns void
 */
export const identifyOrganization = (
  orgId: string,
  properties?: Record<string, unknown>,
) => {
  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    posthog.group(ORG_GROUP_TYPE, orgId, properties);
  }
};

export const reset = () => {
  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    posthog.reset();
  }
  Sentry.setUser(null);
};

/** Records a product analytics event, currently in PostHog. */
export const captureEvent = (
  name: string,
  properties?: Record<string, unknown>,
) => {
  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    posthog.capture(name, properties);
  }
};

/** Provider-neutral detail for a captured error. Tags are indexed and searchable; extra is free-form. */
export type ExceptionContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

/** Reports an error to every configured error tracker, currently Sentry. */
export const captureException = (error: Error, context?: ExceptionContext) => {
  if (!import.meta.env.PROD) {
    console.error(error);
  }
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
  });
};
